import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";
import TotalclientIcon from "../../assets/client/total_clients.svg";
import "../../styles/clientPage.css";
import { Box, CircularProgress } from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const ClientPortfolioHealthTrendLine = ({clientHealthTrendLineData, showSpinner}: {clientHealthTrendLineData: any, showSpinner: boolean}) => {
  const chartData =
    clientHealthTrendLineData?.map((item: any) => ({
        month: new Date(`${item.month}-01`).toLocaleString(
        "default",
        { month: "short" }
        ),
        totalClients: item.totalClients,
        promoters: item.promoterClients,
        neutrals: item.neutralClients,
        detractors: item.detractorClients,
        notRated: item.notRatedClients,

        percentage:
        item.totalClients > 0
            ? Number(
                (
                (item.detractorClients /
                    item.totalClients) *
                100
                ).toFixed(1)
            )
            : 0,
    })) || [];

  const data = {
    labels: chartData.map((item: any) => item.month),
    datasets: [
        {
            label: "",
            data: chartData.map((item: any) => item.percentage),
            borderColor: "#FF5A5F",
            borderWidth: 1,
            borderDash: [4, 4],
            pointRadius: 5,
            pointHoverRadius: 5,
            pointBackgroundColor: "#FFFFFF",
            pointBorderColor: "#FF5A5F",
            pointHoverBackgroundColor: "#FFC8C8",
            pointHoverBorderColor: "#FF5A5F",
            pointHoverBorderWidth: 1,
            tension: 0.35,
            clip: false,
        },
    ],
  };

  const externalTooltipHandler = (context: any) => {
    const { chart, tooltip } = context;
    let tooltipEl = document.getElementById(
        "portfolio-health-tooltip"
    );
    if (!tooltipEl) {
        tooltipEl = document.createElement("div");
        tooltipEl.id = "portfolio-health-tooltip";
        document.body.appendChild(tooltipEl);
    }
    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = "0";
        return;
    }
    const index = tooltip.dataPoints?.[0]?.dataIndex;

    if (index === undefined) return;
    const data = chartData[index];
    tooltipEl.innerHTML = `
        <div class="portfolio-tooltip-card">
        <div class="portfolio-tooltip-header">
            <img src=${TotalclientIcon} alt="Total Clients"/>
            <span>Total Clients: ${data.totalClients}</span>
        </div>

        <div class="portfolio-tooltip-row">
            <span class="tooltip-square detractor"></span>
            Detractors: ${data.detractors}
            <span class="tooltip-percent">
            (${data.percentage}%)
            </span>
        </div>

        <div class="portfolio-tooltip-row">
            <span class="tooltip-square neutral"></span>
            Neutrals: ${data.neutrals}
            <span class="tooltip-percent">
            (${(
                (data.neutrals / data.totalClients) *
                100
            ).toFixed(0)}%)
            </span>
        </div>

        <div class="portfolio-tooltip-row">
            <span class="tooltip-square promoter"></span>
            Promoters: ${data.promoters}
            <span class="tooltip-percent">
            (${(
                (data.promoters / data.totalClients) *
                100
            ).toFixed(0)}%)
            </span>
        </div>
        <div class="portfolio-tooltip-row">
            <span class="tooltip-square notRated"></span>
            Not Rated: ${data.notRated}
            <span class="tooltip-percent">
            (${(
                (data.notRated / data.totalClients) *
                100
            ).toFixed(0)}%)
            </span>
        </div>
        </div>
    `;

    const rect = chart.canvas.getBoundingClientRect();

    tooltipEl.style.opacity = "1";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.pointerEvents = "none";

    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;
    let left =
    rect.left +
    window.scrollX +
    tooltip.caretX -
    tooltipWidth / 2;

    const minLeft = rect.left + window.scrollX;
    const maxLeft =
    rect.left +
    window.scrollX +
    rect.width -
    tooltipWidth;

    left = Math.max(minLeft, Math.min(left, maxLeft));

    tooltipEl.style.left = `${left}px`;

    const gap = 12;

    let top =
    rect.top +
    window.scrollY +
    tooltip.caretY -
    tooltipHeight -
    gap;

    if (top < rect.top + window.scrollY) {
    top =
        rect.top +
        window.scrollY +
        tooltip.caretY +
        gap;
    }

    tooltipEl.style.top = `${top}px`;
    };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
        mode: "nearest",
        intersect: true,
    },

    layout: {
      padding: {
        top: 25,
        right: 20,
        left: 0,
        bottom: 25,
      },
    },

    plugins: {
        legend: {
            display: false,
        },
        datalabels: {
            color: "#FF5A5F",
            anchor: "end",
            align: "top",
            offset: 4,
            formatter: (value: number) => `${value}%`,
        },
        tooltip: {
            enabled: false,
            external: externalTooltipHandler,
        },
    },

    scales: {
      x: {
        offset: true,
        border: {
          display: false,
        },

        grid: {
          display: false,
        },

        ticks: {
          color: "#575763",
          font: {
            size: 12,
          },
        },
      },

      y: {
        min: 0,
        max: 100,
        border: {
          display: false,
        },

        grid: {
          color: "#D9D9D9",
          drawTicks: false,
        },

        ticks: {
          stepSize: 20,
          color: "#000000",

          callback: (value: any) => `${value}%`,
        },
      },
    },
  };

  return (
    <div className="section portfolio-health-section">
      <div className="section__header">
        <h2 className="section__title">
          Portfolio Health Trend
        </h2>

        <div className="section__controls">
          <span className="section__dropdown">
            Trailing 6 Months
          </span>
        </div>
      </div>

      <div className="portfolio-health-chart">
        {showSpinner ? (
            <Box className="portfolio-health-loader">
            <CircularProgress />
            </Box>
        ) : (
            <Line data={data as any} options={options} />
        )}
      </div>
    </div>
  );
};

export default ClientPortfolioHealthTrendLine;